import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { UserService } from "../../../services/UserService";
import { apiMessage } from "../../../api/apiMessage";
import useAlive from "../../../hooks/useAlive";
import { modeLabel, typeLabel, typesIn } from "./constants";

const RANGES = [
  { hours: 24, label: "24 hours" },
  { hours: 168, label: "7 days" },
];

// A run's outcome, as the API names them.
const OUTCOMES = [
  { key: "ok", label: "OK" },
  { key: "partial", label: "Partial" },
  { key: "failed", label: "Failed" },
  { key: "missed", label: "Missed" },
  { key: "paused", label: "Paused" },
  { key: "skipped", label: "Skipped" },
  { key: "dryRun", label: "Shadow" },
];

const outcomeLabel = (key) => OUTCOMES.find((outcome) => outcome.key === key)?.label ?? key;
const when = (date) => (date ? moment(date).format("D MMM, HH:mm") : "—");
const number = (value) => (Number(value) || 0).toLocaleString();

const Home = ({ item }) =>
  item.homeName ? <span>{item.homeName}</span> : <span className="hx-adm-mono">{item.homeId}</span>;

// What the switches are now, in one line.
const switchesText = (switches) => {
  if (!switches) return "";
  const where = switches.enabled
    ? "On for every home"
    : switches.pilotHomes
    ? `On for ${switches.pilotHomes} test home${switches.pilotHomes === 1 ? "" : "s"}`
    : "Off for every home";
  const byMode = (mode) =>
    Object.entries(switches.types ?? {})
      .filter(([, value]) => value === mode)
      .map(([type]) => typeLabel(type));
  const modes = ["new", "shadow"]
    .map((mode) => (byMode(mode).length ? `${modeLabel(mode)}: ${byMode(mode).join(", ")}` : null))
    .filter(Boolean);
  return [where, `notifications ${switches.notificationsEnabled ? "on" : "off"}`, ...modes].join(" · ");
};

// One of the lists under the tiles, folded unless it matters.
const List = ({ title, count, items, open, note, head, row }) => (
  <details className="hx-adm-list" open={open && count > 0}>
    <summary>
      <span className="hx-adm-list-title">{title}</span>
      <span className={`hx-adm-counts ${count ? "is-alert" : ""}`}>
        {number(count)}
        {items.length < count ? ` · showing ${items.length} of ${number(count)}` : ""}
      </span>
    </summary>
    {note ? <p className="hx-adm-note">{note}</p> : null}
    {items.length ? (
      <div className="hx-adm-table-wrap">
        <table className="hx-adm-table">
          <thead>
            <tr>
              {head.map((label) => (
                <th key={label} scope="col">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{items.map(row)}</tbody>
        </table>
      </div>
    ) : (
      <p className="hx-adm-note">None.</p>
    )}
  </details>
);

// The summary's lists of runs and automations, each row with its home.
const LISTS = ["problems", "overdue", "paused", "review", "sunWithoutOldJob"];
const homesIn = (summary) =>
  LISTS.flatMap((key) => summary?.[key] ?? []).map((item) => ({ homeId: item.homeId, name: item.homeName }));

// How the new engine's automations have been running: counts for the chosen
// time, a row per type, and the runs and automations that need a look. It
// loads when opened and on Reload, never by itself. onNames gets the homes
// the summary names, so the page can show test homes by name.
function Health({ onNames }) {
  const alive = useAlive();
  const [hours, setHours] = useState(24);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (span) => {
      setLoading(true);
      setError(null);
      try {
        const result = await UserService.getAutomationSummary(span);
        if (alive.current) {
          setSummary(result);
          if (onNames) onNames(homesIn(result));
        }
      } catch (err) {
        if (alive.current) setError(apiMessage(err, "Couldn't load the run summary."));
      } finally {
        if (alive.current) setLoading(false);
      }
    },
    [alive, onNames]
  );

  useEffect(() => {
    load(hours);
  }, [load, hours]);

  const controls = (
    <div className="hx-adm-health-bar">
      <div className="hx-adm-seg" role="radiogroup" aria-label="Time">
        {RANGES.map((range) => (
          <label key={range.hours} className={hours === range.hours ? "is-active" : ""}>
            <input
              type="radio"
              name="hx-adm-health-hours"
              value={range.hours}
              checked={hours === range.hours}
              disabled={loading}
              onChange={() => setHours(range.hours)}
            />
            <span>{range.label}</span>
          </label>
        ))}
      </div>
      <button type="button" className="hx-btn is-ghost" disabled={loading} onClick={() => load(hours)}>
        {loading ? "Loading…" : "Reload"}
      </button>
    </div>
  );

  if (!summary) {
    return (
      <>
        {controls}
        {error ? (
          <p className="hx-adm-note is-error" role="alert">
            {error} The run summary needs the API version that has it.
          </p>
        ) : (
          <div className="hx-skel" style={{ height: 160 }} aria-label="Loading" />
        )}
      </>
    );
  }

  const runs = summary.runs ?? {};
  const counts = summary.counts ?? {};
  const outcomes = runs.byOutcome ?? {};
  const tiles = [
    { label: "Runs", value: runs.total, note: runs.truncated ? "newest 20,000 only" : null },
    { label: "OK", value: outcomes.ok },
    { label: "Problems", value: counts.problems, alert: true },
    { label: "Overdue", value: counts.overdue, alert: true },
    { label: "Paused", value: counts.paused },
    { label: "To review", value: counts.review },
  ];
  const types = typesIn({ ...summary.automations?.byType, ...runs.byType }).filter(
    ({ key }) => summary.automations?.byType?.[key]?.total || Object.values(runs.byType?.[key] ?? {}).some(Boolean)
  );

  return (
    <div className={loading ? "hx-adm-refreshing" : ""}>
      {controls}
      <p className="hx-adm-text">
        {switchesText(summary.switches)}. Since {when(summary.since)}, made at {when(summary.generatedAt)}
        {runs.homes ? `; ${number(runs.homes)} home${runs.homes === 1 ? "" : "s"} ran automations` : ""}.
      </p>
      {error ? (
        <p className="hx-adm-note is-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="hx-adm-tiles">
        {tiles.map((tile) => (
          <div key={tile.label} className={`hx-adm-tile ${tile.alert && tile.value > 0 ? "is-error" : ""}`}>
            <span className="hx-adm-tile-value">{number(tile.value)}</span>
            <span className="hx-adm-tile-label">
              {tile.label}
              {tile.note ? ` (${tile.note})` : ""}
            </span>
          </div>
        ))}
      </div>

      {types.length ? (
        <div className="hx-adm-table-wrap">
          <table className="hx-adm-table">
            <caption className="hx-adm-sr">Automations and runs per type</caption>
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col" className="is-number">
                  Automations
                </th>
                <th scope="col" className="is-number">
                  New engine
                </th>
                {OUTCOMES.map((outcome) => (
                  <th key={outcome.key} scope="col" className="is-number">
                    {outcome.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map(({ key, label }) => {
                const automations = summary.automations?.byType?.[key] ?? {};
                const byOutcome = runs.byType?.[key] ?? {};
                return (
                  <tr key={key}>
                    <th scope="row">{label}</th>
                    <td className="is-number">{number(automations.total)}</td>
                    <td className="is-number">{number(automations.newEngine)}</td>
                    {OUTCOMES.map((outcome) => (
                      <td key={outcome.key} className="is-number">
                        {number(byOutcome[outcome.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="hx-adm-empty">No automations and no runs in this time.</p>
      )}

      <List
        title="Problems"
        count={counts.problems ?? 0}
        items={summary.problems ?? []}
        open
        head={["Time", "Home", "Automation", "Type", "Outcome", "Message"]}
        row={(item, index) => (
          <tr key={`${item.automationId}-${item.at}-${index}`}>
            <td>{when(item.at)}</td>
            <td>
              <Home item={item} />
            </td>
            <td>{item.name}</td>
            <td>{typeLabel(item.type)}</td>
            <td>
              <span className={`hx-adm-result is-${item.outcome === "failed" ? "error" : item.outcome === "partial" ? "skip" : "review"}`}>
                <span className="hx-adm-dot" aria-hidden="true" />
                {outcomeLabel(item.outcome)}
              </span>
            </td>
            <td>
              {item.message || "—"}
              {item.source ? <span className="hx-adm-cell-note">{item.source}</span> : null}
            </td>
          </tr>
        )}
      />
      <List
        title="Overdue"
        count={counts.overdue ?? 0}
        items={summary.overdue ?? []}
        open
        note="Due over 15 minutes ago and not run: the engine is behind or down."
        head={["Due", "Home", "Automation", "Type"]}
        row={(item) => (
          <tr key={item.automationId}>
            <td>{when(item.dueAt)}</td>
            <td>
              <Home item={item} />
            </td>
            <td>{item.name}</td>
            <td>{typeLabel(item.type)}</td>
          </tr>
        )}
      />
      <List
        title="Paused"
        count={counts.paused ?? 0}
        items={summary.paused ?? []}
        head={["Since", "Home", "Automation", "Type", "Reason"]}
        row={(item) => (
          <tr key={item.automationId}>
            <td>{when(item.since)}</td>
            <td>
              <Home item={item} />
            </td>
            <td>{item.name}</td>
            <td>{typeLabel(item.type)}</td>
            <td>{item.reason || "—"}</td>
          </tr>
        )}
      />
      <List
        title="To review"
        count={counts.review ?? 0}
        items={summary.review ?? []}
        head={["Home", "Automation", "Type", "Reason"]}
        row={(item) => (
          <tr key={item.automationId}>
            <td>
              <Home item={item} />
            </td>
            <td>{item.name}</td>
            <td>{typeLabel(item.type)}</td>
            <td>{item.reason || "—"}</td>
          </tr>
        )}
      />
      <List
        title="Sunrise/sunset scenes without an old job"
        count={counts.sunWithoutOldJob ?? 0}
        items={summary.sunWithoutOldJob ?? []}
        note="Sunrise/sunset scenes the old scene worker had no job for, so they weren't running before the switch. New engine: yes means the new engine runs them now."
        head={["Home", "Automation", "On", "Status", "New engine"]}
        row={(item) => (
          <tr key={item.automationId}>
            <td>
              <Home item={item} />
            </td>
            <td>{item.name}</td>
            <td>{item.enabled ? "Yes" : "No"}</td>
            <td>{item.status || "—"}</td>
            <td>{item.newEngine ? "Yes" : "No"}</td>
          </tr>
        )}
      />
    </div>
  );
}

export default Health;
